import CommunicationManager from "../../Helper/CommunicationManager";
import Communicator from "../../Helper/Communicator";
import { useAppViewStore } from "../../Stores/AppView";
import { useFormStore } from "../../Stores/Form";
import { useRouterStore } from "../../Stores/Router";
import { useTicketStore } from "../../Stores/Ticket";

// How long the bug report workflow's first step may take to arrive before the
// conversation is reloaded for it.
const BUG_REPORT_STEP_REFETCH_MS = 2500;

export function executeAIAction(action: any) {
  const data = { ...(action.data ?? {}) };
  const name = action.name ?? "";
  delete data.actionButtonTitle;

  if (name === "reportabug") {
    // The project's bug report workflow (the server names it on the button
    // while it is live): it takes over this conversation from Kai's answer
    // step, with Kai's summary as the bug's description. Anywhere else — no
    // workflow, or Kai no longer the waiting step — the classic form opens.
    const ticketStore = useTicketStore.getState();
    if (
      typeof data?.workflowId === "string" &&
      data.workflowId.length > 0 &&
      ticketStore.currentBotAction?.action?.type === "answerbotflow"
    ) {
      const label = action.actionButtonTitle ?? "";
      const ticketId = ticketStore.currentTicket?.id;
      ticketStore
        .answerBotAction(
          {
            message: label,
            question: label,
            botAction: "reportBug",
            workflowId: data.workflowId,
            description: data?.description ?? "",
          },
          [],
        )
        .then((result) => {
          if (!result?.sent) {
            return;
          }
          // The workflow's first step normally arrives over realtime. A
          // conversation with Kai's answers skips the store's missing-step
          // refetch, so look once ourselves if it hasn't shown up.
          setTimeout(() => {
            const state = useTicketStore.getState();
            if (
              state.currentTicket?.id === ticketId &&
              !state.currentBotAction?.action
            ) {
              state
                .loadComments(true)
                // The step it loads ends the "typing" Kai showed for the tap.
                .then(() => useTicketStore.getState().clearUserTyping())
                .catch(() => {});
            }
          }, BUG_REPORT_STEP_REFETCH_MS);
        })
        .catch(() => {});
      return;
    }

    const flow = "bugreporting";
    useFormStore.setState({
      feedbackFlow: flow,
      isSurveyFlow: false,
      preFillData: {
        description: data?.description ?? "",
      },
    });
    useRouterStore.getState().pushPage("flow", {
      flowId: flow,
      handoffTicketId: useTicketStore.getState().currentTicket?.id,
    });
    useAppViewStore.getState().reset();
    return;
  }

  if (name === "createfeaturerequest") {
    const flow = "featurerequests";
    useFormStore.setState({
      feedbackFlow: flow,
      isSurveyFlow: false,
      preFillData: {
        description: data?.description ?? "",
        title: data?.title ?? "",
      },
    });
    useRouterStore.getState().pushPage("flow", {
      flowId: flow,
      handoffTicketId: useTicketStore.getState().currentTicket?.id,
    });
    useAppViewStore.getState().reset();
    return;
  }

  if (name === "producttour") {
    Communicator.openURL(data.url, false, { placeholders: false });
    Communicator.closeWidget();
    return;
  }

  if (name === "sendMessage") {
    const message = data?.message ?? action.actionButtonTitle ?? "";
    useTicketStore.getState().answerBotAction({ question: message }, []);
    return;
  }

  if (name === "humanHandoff") {
    const message = action.actionButtonTitle ?? "";
    useTicketStore
      .getState()
      .answerBotAction(
        { message, question: message, botAction: "humanHandoff" },
        [],
      );
    return;
  }

  // "Yes, please" on Kai's offer to escalate to Kai Resolve (CONFIRMATION
  // mode). Structural botAction, same transport as humanHandoff — the server
  // runs the PASS_TO_KAI_RESOLVE capability branch on it.
  if (name === "kaiResolveConfirm") {
    const message = action.actionButtonTitle ?? "";
    useTicketStore
      .getState()
      .answerBotAction(
        { message, question: message, botAction: "kaiResolveConfirm" },
        [],
      );
    return;
  }

  if (name === "openUrl") {
    const url = data?.url ?? "";
    if (/^https:\/\//i.test(url)) {
      Communicator.openURL(url, true, { placeholders: false });
    }
    return;
  }

  if (name === "workflow") {
    const workflowId = data?.workflowId ?? "";
    const label = action.actionButtonTitle ?? "";
    useTicketStore
      .getState()
      .answerBotAction(
        { message: label, question: label, botAction: "workflow", workflowId },
        [],
      );
    return;
  }

  CommunicationManager.getInstance().sendMessage({
    name: "tool-execution",
    data: {
      name: name,
      params: data,
    },
  });
}
